import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { LuChevronDown } from '@platform/icon';
import { TABLE_GRID_MAX } from './editorOptions.ts';

// 툴바를 눌러도 본문 selection 이 풀리지 않게 한다.
const keepSelection = (e: React.MouseEvent) => e.preventDefault();

const buttonBase = [
  'inline-flex h-8 min-w-8 items-center justify-center gap-0.5 rounded px-1.5 text-sm font-medium transition-colors',
  'disabled:opacity-40 disabled:cursor-not-allowed',
].join(' ');

const buttonTone = (active?: boolean) =>
  active
    ? 'bg-neutral-950 text-white dark:bg-white dark:text-neutral-950'
    : 'text-neutral-700 dark:text-neutral-300 hover:bg-black/[0.06] dark:hover:bg-white/10';

interface ToolButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  /** 툴팁에 붙는 단축키 (예: Ctrl+B) */
  shortcut?: string;
  children: ReactNode;
}

export function ToolButton({ active, disabled, onClick, label, shortcut, children }: ToolButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={keepSelection}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={shortcut ? `${label} (${shortcut})` : label}
      className={`${buttonBase} ${buttonTone(active)}`}
    >
      {children}
    </button>
  );
}

export function ToolDivider() {
  return <span className="mx-1 h-5 w-px bg-black/10 dark:bg-white/15" aria-hidden />;
}

interface ToolSelectProps {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  /** 붙여넣기 등으로 목록에 없는 값이 들어왔을 때 표시할 이름 */
  unknownLabel?: (value: string) => string;
  className?: string;
  /** 각 항목을 그 글꼴로 미리 보여준다 */
  previewFont?: boolean;
}

export function ToolSelect({
  label,
  value,
  options,
  onChange,
  unknownLabel,
  className,
  previewFont,
}: ToolSelectProps) {
  const known = options.some((o) => o.value === value);
  return (
    <select
      aria-label={label}
      title={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={[
        'h-8 rounded border border-black/10 bg-white px-1.5 text-sm text-neutral-800 outline-none',
        'focus:border-neutral-950 dark:border-white/15 dark:bg-dark-base dark:text-neutral-200 dark:focus:border-white/50',
        className ?? '',
      ].join(' ')}
    >
      {!known && value && <option value={value}>{unknownLabel?.(value) ?? value}</option>}
      {options.map((o) => (
        <option
          key={o.value}
          value={o.value}
          style={previewFont && o.value ? { fontFamily: o.value } : undefined}
        >
          {o.label}
        </option>
      ))}
    </select>
  );
}

interface ToolPopoverProps {
  label: string;
  trigger: ReactNode;
  active?: boolean;
  disabled?: boolean;
  /** 패널을 트리거 오른쪽 끝에 맞춘다(툴바 끝쪽 버튼용) */
  alignEnd?: boolean;
  /** 제어형으로 쓸 때(단축키로 열기 등) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: (close: () => void) => ReactNode;
}

/** 드롭다운 패널. 바깥 클릭·Esc 로 닫힌다. */
export function ToolPopover({
  label,
  trigger,
  active,
  disabled,
  alignEnd,
  open: controlledOpen,
  onOpenChange,
  children,
}: ToolPopoverProps) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const setOpen = (next: boolean) => {
    setInnerOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => {
      setInnerOpen(false);
      onOpenChange?.(false);
    };
    const onPointer = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  // 툴바가 여러 줄로 접히는 좁은 화면에서 패널이 뷰포트 밖으로 나가지 않게 가로로 밀어 넣는다.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const gutter = 8;
    const rect = panel.getBoundingClientRect();
    const shift =
      rect.left < gutter
        ? gutter - rect.left
        : Math.min(0, window.innerWidth - gutter - rect.right);
    panel.style.translate = shift ? `${shift}px 0` : '';
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onMouseDown={keepSelection}
        onClick={() => setOpen(!open)}
        disabled={disabled}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={label}
        className={`${buttonBase} ${buttonTone(active || open)}`}
      >
        {trigger}
        <LuChevronDown className="size-3 opacity-60" aria-hidden />
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={label}
          className={[
            'absolute top-full z-40 mt-1 w-max max-w-[calc(100vw-1rem)] rounded-lg border border-black/10 bg-white p-2 shadow-lg',
            'dark:border-white/15 dark:bg-dark-surface',
            alignEnd ? 'right-0' : 'left-0',
          ].join(' ')}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

interface ColorGridProps {
  rows: readonly (readonly string[])[];
  /** 현재 색 (선택 표시용) */
  value?: string;
  /** null = 색 해제 */
  onPick: (color: string | null) => void;
  resetLabel: string;
}

export function ColorGrid({ rows, value, onPick, resetLabel }: ColorGridProps) {
  const current = value?.toLowerCase();
  return (
    <div className="w-[15.5rem] space-y-2">
      <button
        type="button"
        onMouseDown={keepSelection}
        onClick={() => onPick(null)}
        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-neutral-700 hover:bg-black/[0.06] dark:text-neutral-200 dark:hover:bg-white/10"
      >
        <span className="size-4 rounded-sm border border-black/20 bg-[linear-gradient(135deg,transparent_45%,#ef4444_45%,#ef4444_55%,transparent_55%)] dark:border-white/30" />
        {resetLabel}
      </button>
      <div className="space-y-1">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-1">
            {row.map((color) => (
              <button
                key={color}
                type="button"
                onMouseDown={keepSelection}
                onClick={() => onPick(color)}
                aria-label={color}
                title={color}
                className={[
                  'size-5 rounded-sm border transition-transform hover:scale-110',
                  current === color
                    ? 'border-neutral-950 ring-2 ring-neutral-950/30 dark:border-white dark:ring-white/40'
                    : 'border-black/15 dark:border-white/20',
                ].join(' ')}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        ))}
      </div>
      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-neutral-700 hover:bg-black/[0.06] dark:text-neutral-200 dark:hover:bg-white/10">
        <input
          type="color"
          value={current && /^#[0-9a-f]{6}$/.test(current) ? current : '#000000'}
          onChange={(e) => onPick(e.target.value)}
          className="size-5 cursor-pointer rounded-sm border-0 bg-transparent p-0"
        />
        다른 색…
      </label>
    </div>
  );
}

/** 마우스로 행×열을 고르는 표 삽입 격자 */
export function TableGridPicker({ onPick }: { onPick: (rows: number, cols: number) => void }) {
  const [hover, setHover] = useState({ rows: 0, cols: 0 });
  return (
    <div className="space-y-1.5" onMouseLeave={() => setHover({ rows: 0, cols: 0 })}>
      <div
        className="grid gap-0.5"
        style={{ gridTemplateColumns: `repeat(${TABLE_GRID_MAX}, 1.125rem)` }}
      >
        {Array.from({ length: TABLE_GRID_MAX * TABLE_GRID_MAX }, (_, i) => {
          const r = Math.floor(i / TABLE_GRID_MAX) + 1;
          const c = (i % TABLE_GRID_MAX) + 1;
          const on = r <= hover.rows && c <= hover.cols;
          return (
            <button
              key={i}
              type="button"
              onMouseDown={keepSelection}
              onMouseEnter={() => setHover({ rows: r, cols: c })}
              onFocus={() => setHover({ rows: r, cols: c })}
              onClick={() => onPick(r, c)}
              aria-label={`${r}행 ${c}열 표`}
              className={[
                'size-[1.125rem] rounded-[2px] border',
                on
                  ? 'border-neutral-950 bg-neutral-950/15 dark:border-white dark:bg-white/25'
                  : 'border-black/15 dark:border-white/20',
              ].join(' ')}
            />
          );
        })}
      </div>
      <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
        {hover.rows ? `${hover.rows} × ${hover.cols} 표` : '표 크기를 고르세요'}
      </p>
    </div>
  );
}

interface LinkFormProps {
  initialUrl: string;
  /** 선택 영역이 없을 때만 표시 텍스트 입력을 보인다 */
  withText: boolean;
  onSubmit: (url: string, text: string) => void;
  onRemove?: () => void;
}

const fieldClass =
  'block w-full rounded border border-black/15 bg-white px-2 py-1.5 text-sm outline-none focus:border-neutral-950 dark:border-white/15 dark:bg-dark-base dark:text-neutral-100 dark:focus:border-white/50';

export function LinkForm({ initialUrl, withText, onSubmit, onRemove }: LinkFormProps) {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState('');
  const submit = () => {
    if (url.trim()) onSubmit(url.trim(), text.trim());
  };
  // <form> 대신 div — 에디터를 감싼 폼이 생기면 중첩 form 이 된다.
  return (
    <div
      className="w-72 space-y-2"
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
        e.preventDefault();
        submit();
      }}
    >
      <label className="block space-y-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">
        주소
        <input
          autoFocus
          type="text"
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          className={fieldClass}
        />
      </label>
      {withText && (
        <label className="block space-y-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">
          표시할 텍스트
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="비우면 주소가 그대로 표시됩니다"
            className={fieldClass}
          />
        </label>
      )}
      <div className="flex justify-end gap-1.5 pt-1">
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="rounded px-2.5 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            링크 제거
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!url.trim()}
          className="rounded bg-neutral-950 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40 dark:bg-white dark:text-neutral-950"
        >
          적용
        </button>
      </div>
    </div>
  );
}

export function SymbolPicker({
  symbols,
  onPick,
}: {
  symbols: readonly string[];
  onPick: (symbol: string) => void;
}) {
  return (
    <div className="grid w-max grid-cols-8 gap-0.5 sm:grid-cols-16">
      {symbols.map((s) => (
        <button
          key={s}
          type="button"
          onMouseDown={keepSelection}
          onClick={() => onPick(s)}
          aria-label={s}
          className="size-7 rounded text-base text-neutral-800 hover:bg-black/[0.06] dark:text-neutral-100 dark:hover:bg-white/10"
        >
          {s}
        </button>
      ))}
    </div>
  );
}
